import gql from 'graphql-tag';

import { Query, Mutation, QueryRegistry } from 'core/index';
import { USER_ERROR_FIELDS } from 'core/errors';

import { PROJECT_CONNECTION_FIELDS } from 'apps/project/gql';
import { STORY_CONNECTION_FIELDS } from 'apps/story/gql';
import { COLLECTION_CONNECTION_FIELDS } from 'apps/collection/gql';
import { THREAD_CONNECTION_FIELDS } from 'apps/forum/gql';
import {
  PROFILE_FIELDS,
  INITIATIVE_FIELDS,
  INITIATIVE_CONNECTION_FIELDS,
} from 'apps/initiative/gql/fragments';

export const INITIATIVE_QUERY = gql`
  ${INITIATIVE_FIELDS}
  ${USER_ERROR_FIELDS}
  query Initiative($slug: String) {
    loggedInUser {
      id
      pendingInvites {
        id
        inviteDate
        status
        group {
          id
          space {
            id
            parent {
              id
              parent {
                id
              }
            }
          }
          name
        }
      }
    }
    initiative(slug: $slug) {
      result {
        ...initiativeFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class InitiativeQuery extends Query {
  static conf = {
    GQL: INITIATIVE_QUERY,
    varNames: ['slug'],
    errorPath: 'initiative.userErrors',
  };
}

export const INITIATIVE_OVERVIEW_QUERY = gql`
  ${PROJECT_CONNECTION_FIELDS}
  ${STORY_CONNECTION_FIELDS}
  ${COLLECTION_CONNECTION_FIELDS}
  ${THREAD_CONNECTION_FIELDS}
  ${PROFILE_FIELDS}
  ${USER_ERROR_FIELDS}
  query Initiative(
    $slug: String
    $membersCount: Int
    $membersCursor: String
    $membersSortBy: String
    $projectsCount: Int
    $projectsCursor: String
    $projectsSortBy: String
    $storiesCount: Int
    $storiesCursor: String
    $storiesSortBy: String
    $threadBatchSize: Int
    $threadCursor: String
    $threadSortBy: String
  ) {
    initiative(slug: $slug) {
      result {
        id
        members(
          first: $membersCount
          after: $membersCursor
          sortBy: $membersSortBy
        ) {
          ...pConnectionFields
        }
        projects(
          first: $projectsCount
          after: $projectsCursor
          sortBy: $projectsSortBy
        ) {
          ...projectConnectionFields
        }
        stories(
          first: $storiesCount
          after: $storiesCursor
          sortBy: $storiesSortBy
        ) {
          ...storyConnectionFields
        }
        forum {
          id
          active
          threads(
            first: $threadBatchSize
            after: $threadCursor
            sortBy: $threadSortBy
          ) {
            ...threadConnectionFields
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class InitiativeOverviewQuery extends Query {
  static conf = {
    GQL: INITIATIVE_OVERVIEW_QUERY,
    varNames: [
      'slug',
      'membersCount',
      'membersCursor',
      'membersSortBy',
      'projectsCount',
      'projectsCursor',
      'projectsSortBy',
      'storiesCount',
      'storiesCursor',
      'storiesSortBy',
      'collectionsCount',
      'collectionsCursor',
      'collectionsSortBy',
    ],
    resultPath: 'initiative.result',
    errorPath: 'initiative.userErrors',
  };
}

const INITIATIVES_QUERY = gql`
  ${USER_ERROR_FIELDS}
  ${INITIATIVE_CONNECTION_FIELDS}
  query Initiatives($batchSize: Int, $cursor: String, $sortBy: String) {
    initiatives(first: $batchSize, after: $cursor, sortBy: $sortBy) {
      result {
        ...initiativeConnectionFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class InitiativesQuery extends Query {
  static conf = {
    GQL: INITIATIVES_QUERY,
    varNames: ['batchSize', 'cursor', 'sortBy'],
    resultPath: 'initiatives.result',
    errorPath: 'initiatives.userErrors',
  };
}

export const INITIATIVE_MUTATION = gql`
  ${INITIATIVE_FIELDS}
  ${USER_ERROR_FIELDS}
  mutation Initiative($initiativeInput: InitiativeInput) {
    initiative(initiativeData: $initiativeInput) {
      initiative {
        ...initiativeFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class InitiativeMutation extends Mutation {
  static conf = {
    GQL: INITIATIVE_MUTATION,
    inputName: 'initiativeInput',
    resultPath: 'initiative.result',
    errorPath: 'initiative.userErrors',
  };
}

const INITIATIVE_MEMBERS = gql`
  ${USER_ERROR_FIELDS}
  query InitiativeMembers(
    $slug: String
    $membersCount: Int
    $membersCursor: String
    $membersSortBy: String
  ) {
    initiative(slug: $slug) {
      result {
        id
        members(
          first: $membersCount
          after: $membersCursor
          sortBy: $membersSortBy
        ) {
          pageInfo {
            startCursor
            endCursor
            hasNextPage
          }
          totalCount
          edges {
            cursor
            node {
              id
              fullName
              username
              imageFallbackChar
              avatar {
                id
                path
                url
                completed
              }
              content {
                id
                whitelabel
              }
            }
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class InitiativeMembersQuery extends Query {
  static conf = {
    GQL: INITIATIVE_MEMBERS,
    varNames: ['slug', 'membersCount', 'membersCursor', 'membersSortBy'],
    resultPath: 'initiative.result.members',
  };
}

export const NEW_INITIATIVE_MUTATION = gql`
  ${USER_ERROR_FIELDS}
  mutation NewInitiative($initiativeInput: InitiativeInput) {
    initiative(initiativeData: $initiativeInput) {
      initiative {
        id
        title
        slug
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class NewInitiativeMutation extends Mutation {
  static conf = {
    GQL: NEW_INITIATIVE_MUTATION,
    inputName: 'initiativeInput',
    resultPath: 'initiative.initiative',
    errorPath: 'initiative.userErrors',
  };
}

export const AUTOCOMPLETE_INITIATIVES = gql`
  query Initiative($complete: String, $first: Int) {
    initiatives(first: $first, contains: ["slug", $complete]) {
      result {
        edges {
          node {
            id
            slug
            avatar {
              id
              path
              url
            }
            imageFallbackChar
          }
        }
      }
    }
  }
`;

export class AutocompleteInitiativesQuery extends Query {
  static conf = {
    GQL: AUTOCOMPLETE_INITIATIVES,
    varNames: ['complete', 'first'],
    resultPath: 'initiatives.result.edges',
  };
}

const INITIATIVE_CHECK = gql`
  ${USER_ERROR_FIELDS}
  query Initiative($slug: String) {
    initiative(slug: $slug) {
      result {
        id
        canUpdate
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class InitiativeCheckQuery extends Query {
  static conf = {
    GQL: INITIATIVE_CHECK,
    varNames: ['slug'],
    resultPath: 'initiative.result',
    errorPath: 'initiative.userErrors',
  };
}

const ORGANIZATION_TYPES = gql`
  query OrganizationTypes {
    organizationTypes {
      id
      name
    }
  }
`;

export class OrganizationTypesQuery extends Query {
  static conf = {
    GQL: ORGANIZATION_TYPES,
    resultPath: 'organizationTypes',
  };
}

QueryRegistry.add(
  'organization.OrganizationTypesQuery',
  OrganizationTypesQuery,
);
